import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Download, 
  Upload, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  HardDrive,
  AlertTriangle,
  RefreshCw,
  Database,
  Calendar,
  Server,
  Play,
  Pause,
  Plus,
  TestTube,
  Shield
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const SauvegardesEnhanced = () => {
  // Existing state
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // New state for bulk selection
  const [selectedBackups, setSelectedBackups] = useState([]);
  
  // New state for schedules and destinations
  const [schedules, setSchedules] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [activeTab, setActiveTab] = useState('backups');
  
  // Dialogs
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [destinationDialogOpen, setDestinationDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  const [restoreBackup, setRestoreBackup] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importLoading, setImportLoading] = useState(false);
  
  // Forms
  const [backupForm, setBackupForm] = useState({
    compression: 'zip',
    include_uploads: true,
    retention_days: 30
  });
  
  const [restoreForm, setRestoreForm] = useState({
    mode: 'full',
    confirm: '',
    include_users: false // Option pour restaurer les utilisateurs
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    cron_expression: '0 2 * * *',
    timezone: 'UTC',
    enabled: true,
    compression: 'zip',
    include_uploads: true,
    retention_days: 30,
    storage_destination_ids: []
  });
  
  const [destinationForm, setDestinationForm] = useState({
    name: '',
    storage_type: 'local',
    hostname: '',
    port: 22,
    username: '',
    password: '',
    ssh_key: '',
    remote_path: '',
    share_name: '',
    domain: 'WORKGROUP'
  });

  useEffect(() => {
    if (activeTab === 'backups') {
      fetchBackups();
    } else if (activeTab === 'schedules') {
      fetchSchedules();
      fetchDestinations();
    } else if (activeTab === 'destinations') {
      fetchDestinations();
    }
  }, [activeTab, page]);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/backup/history?page=${page}&limit=10`);
      setBackups(response.data.backups);
      setTotalPages(response.data.pages);
      
      if (response.data.backups.length > 0) {
        setLastBackup(response.data.backups[0]);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await api.get('/backup/schedules');
      setSchedules(response.data.schedules);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await api.get('/storage-destinations');
      setDestinations(response.data.destinations);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      await api.post('/backup/run', backupForm);
      toast.success('Sauvegarde créée avec succès');
      setBackupDialogOpen(false);
      fetchBackups();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleImportBackup = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    // Validate file type
    if (!importFile.name.endsWith('.zip')) {
      toast.error('Seuls les fichiers ZIP sont acceptés');
      return;
    }

    // Validate file size (max 5GB)
    const maxSize = 5 * 1024 * 1024 * 1024; // 5GB
    if (importFile.size > maxSize) {
      toast.error('Le fichier est trop volumineux (max 5GB)');
      return;
    }

    try {
      setImportLoading(true);
      setImportProgress(0);

      const formData = new FormData();
      formData.append('file', importFile);

      // Upload with progress tracking
      const response = await api.post('/backup/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setImportProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        toast.success('✅ Sauvegarde importée avec succès');
        toast.info(`Taille: ${response.data.info.size_mb} MB - Checksum vérifié`);
        setImportDialogOpen(false);
        setImportFile(null);
        setImportProgress(0);
        fetchBackups();
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleCreateSchedule = async () => {
    try {
      await api.post('/backup/schedules', scheduleForm);
      toast.success('Planification créée avec succès');
      setScheduleDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleToggleSchedule = async (scheduleId, currentEnabled) => {
    try {
      await api.put(`/backup/schedules/${scheduleId}`, { enabled: !currentEnabled });
      toast.success(currentEnabled ? 'Planification désactivée' : 'Planification activée');
      fetchSchedules();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleRunScheduleNow = async (scheduleId) => {
    try {
      await api.post(`/backup/schedules/${scheduleId}/run`);
      toast.success('Sauvegarde démarrée');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Supprimer cette planification ?')) return;
    
    try {
      await api.delete(`/backup/schedules/${scheduleId}`);
      toast.success('Planification supprimée');
      fetchSchedules();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleCreateDestination = async () => {
    try {
      await api.post('/storage-destinations', destinationForm);
      toast.success('Destination créée avec succès');
      setDestinationDialogOpen(false);
      fetchDestinations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleTestDestination = async (destId) => {
    try {
      const response = await api.post(`/storage-destinations/${destId}/test`);
      if (response.data.success) {
        toast.success('✓ Connexion réussie');
      } else {
        toast.error(`Échec: ${response.data.message}`);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteDestination = async (destId) => {
    if (!confirm('Supprimer cette destination ?')) return;
    
    try {
      await api.delete(`/storage-destinations/${destId}`);
      toast.success('Destination supprimée');
      fetchDestinations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Bulk selection handlers
  const handleSelectBackup = (backupId) => {
    setSelectedBackups(prev => {
      if (prev.includes(backupId)) {
        return prev.filter(id => id !== backupId);
      } else {
        return [...prev, backupId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedBackups.length === backups.length) {
      setSelectedBackups([]);
    } else {
      setSelectedBackups(backups.map(b => b.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBackups.length === 0) {
      toast.error('Aucune sauvegarde sélectionnée');
      return;
    }

    if (!confirm(`Voulez-vous vraiment supprimer ${selectedBackups.length} sauvegarde(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      await api.post('/backup/bulk-delete', selectedBackups);
      
      toast.success(`✅ ${selectedBackups.length} sauvegarde(s) supprimée(s)`);
      setSelectedBackups([]);
      fetchBackups();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (backup) => {
    try {
      toast.info('Téléchargement en cours...');
      
      const response = await api.get(`/backup/${backup.id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`✅ ${backup.filename} téléchargé`);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteSingle = async (backupId) => {
    if (!confirm('Voulez-vous vraiment supprimer cette sauvegarde?')) {
      return;
    }

    try {
      await api.delete(`/backup/${backupId}`);
      toast.success('✅ Sauvegarde supprimée');
      fetchBackups();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBytes = (bytes) => {
    // Gérer les valeurs undefined, null ou invalides
    if (bytes === undefined || bytes === null || isNaN(bytes)) return 'N/A';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'in_progress':
      case 'running':
        return <Clock className="h-5 w-5 text-blue-600 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStorageIcon = (type) => {
    switch (type) {
      case 'sftp':
        return <Server className="h-5 w-5 text-blue-600" />;
      case 'smb':
        return <Server className="h-5 w-5 text-purple-600" />;
      default:
        return <HardDrive className="h-5 w-5 text-gray-600" />;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sauvegardes & Restauration</h1>
        <p className="text-gray-600">Gérez les sauvegardes, planifications et destinations de stockage</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="backups">
            <Database className="h-4 w-4 mr-2" />
            Sauvegardes
          </TabsTrigger>
          <TabsTrigger value="schedules">
            <Calendar className="h-4 w-4 mr-2" />
            Planifications
          </TabsTrigger>
          <TabsTrigger value="destinations">
            <Server className="h-4 w-4 mr-2" />
            Destinations
          </TabsTrigger>
        </TabsList>

        {/* Backups Tab */}
        <TabsContent value="backups" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Dernière Sauvegarde
                </CardTitle>
              </CardHeader>
              <CardContent>
                {lastBackup ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(lastBackup.status)}
                        <span className="font-medium">{lastBackup.status}</span>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(lastBackup.created_at)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Taille:</span>
                        <span className="ml-2 font-medium">{formatBytes(lastBackup.size_bytes)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Compression:</span>
                        <span className="ml-2 font-medium">{lastBackup.compression}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Aucune sauvegarde disponible</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions Rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  onClick={() => setBackupDialogOpen(true)}
                  className="w-full"
                  disabled={backupLoading}
                >
                  {backupLoading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <HardDrive className="h-4 w-4 mr-2" />
                  )}
                  Sauvegarder maintenant
                </Button>
                <Button 
                  onClick={() => setImportDialogOpen(true)}
                  variant="outline"
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importer une sauvegarde
                </Button>
                <Button 
                  onClick={fetchBackups}
                  variant="outline"
                  className="w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Backups table */}
          {selectedBackups.length > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-blue-900">
                    {selectedBackups.length} sauvegarde(s) sélectionnée(s)
                  </span>
                  <Button onClick={handleBulkDelete} variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer la sélection
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Historique des Sauvegardes</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">
                          <Checkbox
                            checked={selectedBackups.length === backups.length && backups.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-left py-3 px-4">Fichier</th>
                        <th className="text-left py-3 px-4">Taille</th>
                        <th className="text-left py-3 px-4">Durée</th>
                        <th className="text-left py-3 px-4">Statut</th>
                        <th className="text-right py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-8 text-gray-500">
                            Aucune sauvegarde disponible
                          </td>
                        </tr>
                      ) : (
                        backups.map((backup) => (
                          <tr key={backup.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <Checkbox
                                checked={selectedBackups.includes(backup.id)}
                                onCheckedChange={() => handleSelectBackup(backup.id)}
                              />
                            </td>
                            <td className="py-3 px-4 text-sm">{formatDate(backup.created_at)}</td>
                            <td className="py-3 px-4 text-sm font-mono text-green-600">{backup.filename}</td>
                            <td className="py-3 px-4 text-sm">{formatBytes(backup.size_bytes)}</td>
                            <td className="py-3 px-4 text-sm text-gray-500">N/A</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(backup.status)}
                                <span className="text-sm capitalize">{backup.status === 'completed' ? 'Completed' : backup.status}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  onClick={() => handleDownload(backup)}
                                  size="sm"
                                  variant="ghost"
                                  title="Télécharger"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  onClick={() => {
                                    setRestoreBackup(backup);
                                    setRestoreDialogOpen(true);
                                  }}
                                  size="sm"
                                  className="bg-black hover:bg-gray-800 text-white"
                                  title="Restaurer"
                                  disabled={backup.status !== 'completed'}
                                >
                                  <Upload className="h-4 w-4 mr-1" />
                                  Restaurer
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Planifications de Sauvegarde</CardTitle>
              <Button onClick={() => setScheduleDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle Planification
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {schedules.map((schedule) => (
                  <div key={schedule.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        <div>
                          <h3 className="font-medium">{schedule.name}</h3>
                          <p className="text-sm text-gray-500">
                            Cron: {schedule.cron_expression} ({schedule.timezone})
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRunScheduleNow(schedule.id)}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleSchedule(schedule.id, schedule.enabled)}
                        >
                          {schedule.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className={`px-2 py-1 rounded ${schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {schedule.enabled ? 'Actif' : 'Inactif'}
                      </span>
                      {schedule.next_run_at && (
                        <span className="ml-2">Prochain: {formatDate(schedule.next_run_at)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Destinations Tab */}
        <TabsContent value="destinations" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Destinations de Stockage</CardTitle>
              <Button onClick={() => setDestinationDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle Destination
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {destinations.map((dest) => (
                  <div key={dest.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        {getStorageIcon(dest.storage_type)}
                        <div>
                          <h3 className="font-medium">{dest.name}</h3>
                          <p className="text-sm text-gray-500 capitalize">{dest.storage_type}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTestDestination(dest.id)}
                        >
                          <TestTube className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteDestination(dest.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {dest.hostname && (
                      <p className="text-sm text-gray-600">{dest.hostname}:{dest.port}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Backup Dialog - same as before */}
      <Dialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une Sauvegarde</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Compression</Label>
              <Select
                value={backupForm.compression}
                onValueChange={(value) => setBackupForm({...backupForm, compression: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zip">ZIP</SelectItem>
                  <SelectItem value="tar.gz">TAR.GZ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="include_uploads"
                checked={backupForm.include_uploads}
                onChange={(e) => setBackupForm({...backupForm, include_uploads: e.target.checked})}
              />
              <Label htmlFor="include_uploads">Inclure les fichiers</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackupDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateBackup} disabled={backupLoading}>
              {backupLoading ? 'Création...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouvelle Planification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({...scheduleForm, name: e.target.value})}
                placeholder="Sauvegarde quotidienne"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expression Cron</Label>
                <Input
                  value={scheduleForm.cron_expression}
                  onChange={(e) => setScheduleForm({...scheduleForm, cron_expression: e.target.value})}
                  placeholder="0 2 * * *"
                />
                <p className="text-xs text-gray-500">Ex: "0 2 * * *" = Tous les jours à 2h</p>
              </div>
              <div className="space-y-2">
                <Label>Fuseau horaire</Label>
                <Input
                  value={scheduleForm.timezone}
                  onChange={(e) => setScheduleForm({...scheduleForm, timezone: e.target.value})}
                  placeholder="UTC"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Destinations</Label>
              <Select
                value={scheduleForm.storage_destination_ids[0] || ''}
                onValueChange={(value) => setScheduleForm({...scheduleForm, storage_destination_ids: [value]})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une destination" />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map(dest => (
                    <SelectItem key={dest.id} value={dest.id}>{dest.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateSchedule}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Destination Dialog */}
      <Dialog open={destinationDialogOpen} onOpenChange={setDestinationDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouvelle Destination</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input
                value={destinationForm.name}
                onChange={(e) => setDestinationForm({...destinationForm, name: e.target.value})}
                placeholder="Serveur SFTP Principal"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={destinationForm.storage_type}
                onValueChange={(value) => setDestinationForm({...destinationForm, storage_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local</SelectItem>
                  <SelectItem value="sftp">SFTP/SSH</SelectItem>
                  <SelectItem value="smb">SMB/CIFS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {destinationForm.storage_type !== 'local' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hostname</Label>
                    <Input
                      value={destinationForm.hostname}
                      onChange={(e) => setDestinationForm({...destinationForm, hostname: e.target.value})}
                      placeholder="backup.example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={destinationForm.port}
                      onChange={(e) => setDestinationForm({...destinationForm, port: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    value={destinationForm.username}
                    onChange={(e) => setDestinationForm({...destinationForm, username: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={destinationForm.password}
                    onChange={(e) => setDestinationForm({...destinationForm, password: e.target.value})}
                  />
                </div>
                {destinationForm.storage_type === 'sftp' && (
                  <div className="space-y-2">
                    <Label>SSH Private Key (optionnel)</Label>
                    <Textarea
                      value={destinationForm.ssh_key}
                      onChange={(e) => setDestinationForm({...destinationForm, ssh_key: e.target.value})}
                      placeholder="-----BEGIN RSA PRIVATE KEY-----"
                      rows={4}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Chemin distant</Label>
                  <Input
                    value={destinationForm.remote_path}
                    onChange={(e) => setDestinationForm({...destinationForm, remote_path: e.target.value})}
                    placeholder="/backups"
                  />
                </div>
                {destinationForm.storage_type === 'smb' && (
                  <div className="space-y-2">
                    <Label>Nom du partage</Label>
                    <Input
                      value={destinationForm.share_name}
                      onChange={(e) => setDestinationForm({...destinationForm, share_name: e.target.value})}
                      placeholder="backups"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDestinationDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateDestination}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Backup Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importer une Sauvegarde</DialogTitle>
            <DialogDescription>
              Importez un fichier de sauvegarde ZIP pour restaurer l'application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File drop zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                importFile ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 hover:border-emerald-400'
              }`}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
            >
              <input
                type="file"
                id="backup-file-input"
                accept=".zip"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {importFile ? (
                <div className="space-y-2">
                  <CheckCircle className="h-12 w-12 mx-auto text-emerald-600" />
                  <p className="font-medium">{importFile.name}</p>
                  <p className="text-sm text-gray-500">
                    Taille: {(importFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImportFile(null)}
                  >
                    Changer de fichier
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 mx-auto text-gray-400" />
                  <p className="text-gray-600">
                    Glissez-déposez votre fichier ZIP ici
                  </p>
                  <p className="text-sm text-gray-500">ou</p>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('backup-file-input').click()}
                  >
                    Parcourir les fichiers
                  </Button>
                  <p className="text-xs text-gray-400 mt-2">
                    Taille maximale: 5 GB • Format: ZIP uniquement
                  </p>
                </div>
              )}
            </div>

            {/* Progress bar */}
            {importLoading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Importation en cours...</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <div className="flex gap-2">
                <Shield className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium text-blue-900">Vérifications automatiques</p>
                  <ul className="text-blue-700 space-y-1 ml-4 list-disc">
                    <li>Intégrité du fichier ZIP</li>
                    <li>Calcul et vérification du checksum SHA-256</li>
                    <li>Sauvegarde automatique avant restauration</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-900">Important</p>
                  <p className="text-amber-700">
                    Après l'import, utilisez le bouton "Restaurer" pour appliquer la sauvegarde.
                    Une copie de sécurité des données actuelles sera créée automatiquement.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setImportDialogOpen(false);
                setImportFile(null);
                setImportProgress(0);
              }}
              disabled={importLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleImportBackup} 
              disabled={!importFile || importLoading}
            >
              {importLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importation...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Backup Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restaurer la Sauvegarde</DialogTitle>
            <DialogDescription>
              Cette action va restaurer les données depuis la sauvegarde sélectionnée
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {restoreBackup && (
              <div className="bg-gray-50 border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Sauvegarde:</span>
                  <span className="text-sm text-gray-600">{restoreBackup.filename}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Date:</span>
                  <span className="text-sm text-gray-600">
                    {new Date(restoreBackup.created_at).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Taille:</span>
                  <span className="text-sm text-gray-600">
                    {(restoreBackup.size_bytes / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </div>
                {restoreBackup.checksum_sha256 && (
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Checksum:</span>
                    <span className="text-xs text-gray-500 font-mono">
                      {restoreBackup.checksum_sha256.substring(0, 16)}...
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Mode de Restauration</Label>
              <Select
                value={restoreForm.mode}
                onValueChange={(value) => setRestoreForm({...restoreForm, mode: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Restauration Complète</SelectItem>
                  <SelectItem value="data_only">Données uniquement</SelectItem>
                  <SelectItem value="uploads_only">Fichiers uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Option pour inclure les utilisateurs */}
            <div className="flex items-start space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="include-users-enhanced"
                checked={restoreForm.include_users}
                onChange={(e) => setRestoreForm({...restoreForm, include_users: e.target.checked})}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <div className="flex-1">
                <Label htmlFor="include-users-enhanced" className="font-medium text-amber-800 cursor-pointer">
                  Inclure les utilisateurs
                </Label>
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Restaurer les utilisateurs peut invalider les sessions actives. Les mots de passe seront préservés.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="restore-confirm">
                Tapez <span className="font-bold">RESTAURER</span> pour confirmer
              </Label>
              <Input
                id="restore-confirm"
                value={restoreForm.confirm}
                onChange={(e) => setRestoreForm({...restoreForm, confirm: e.target.value})}
                placeholder="RESTAURER"
              />
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900">Attention</p>
                  <p className="text-red-700">
                    La restauration va remplacer les données actuelles. Une sauvegarde de sécurité sera créée automatiquement avant la restauration.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setRestoreDialogOpen(false);
                setRestoreForm({ mode: 'full', confirm: '', include_users: false });
              }}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button 
              onClick={async () => {
                if (restoreForm.confirm !== 'RESTAURER') {
                  toast.error('Veuillez taper RESTAURER pour confirmer');
                  return;
                }
                
                try {
                  setLoading(true);
                  const response = await api.post('/backup/restore', {
                    backup_id: restoreBackup.id,
                    mode: restoreForm.mode,
                    confirm: restoreForm.confirm,
                    include_users: restoreForm.include_users
                  });
                  
                  if (response.data.success) {
                    toast.success('✅ Restauration réussie');
                    toast.info(`Snapshot créé: ${response.data.snapshot_id}`);
                    if (restoreForm.include_users) {
                      toast.warning('Utilisateurs restaurés - reconnectez-vous si nécessaire');
                    }
                    setRestoreDialogOpen(false);
                    setRestoreForm({ mode: 'full', confirm: '', include_users: false });
                    fetchBackups();
                  }
                } catch (error) {
                  toast.error(formatErrorMessage(error));
                } finally {
                  setLoading(false);
                }
              }}
              disabled={restoreForm.confirm !== 'RESTAURER' || loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Restauration...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Restaurer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SauvegardesEnhanced;
